import type { CascadeMarketId } from '@/features/bidding/types/market'
import {
  DEFAULT_PRICES_FCR,
  DEFAULT_PRICES_MFRR,
  MAX_BID_LEVELS_FCR,
  MAX_BID_LEVELS_MFRR,
} from '@/features/bidding/utils/bidLadderInput/constants'

/**
 * Determines if a cascade market uses the activation groups concept.
 * Only mFRR uses activation groups in the cascading bid flow.
 */
export function hasActivationGroupsConcept(market: CascadeMarketId): boolean {
  return market === 'mfrr'
}

/**
 * Determines if drag and drop is enabled for the market.
 * Only mFRR supports drag and drop for moving groups between bid levels.
 */
export function hasDragDropEnabled(market: CascadeMarketId): boolean {
  return market === 'mfrr'
}

/**
 * Determines if a cascade market is an FCR market (FCR-D or FCR-N).
 */
export function isFcrMarket(market: CascadeMarketId): boolean {
  return market === 'fcr-d' || market === 'fcr-n'
}

type VolumeGranularity = {
  stepMW: number
  decimalPlaces: number
}

/**
 * Returns the volume granularity for a market.
 * FCR markets (FCR-D, FCR-N): 100 kW = 0.1 MW granularity (1 decimal place)
 * mFRR / default: 1 MW granularity (integers only, no decimals)
 */
export function getMarketVolumeGranularity(market: CascadeMarketId | string): VolumeGranularity {
  if (market === 'fcr-d' || market === 'fcr-n') {
    return { stepMW: 0.1, decimalPlaces: 1 }
  }
  return { stepMW: 1, decimalPlaces: 0 }
}

/**
 * Returns the bid level configuration for a market (max levels and default prices).
 */
export function getBidLevelConfig(market: CascadeMarketId): { maxBidLevels: number; defaultPrices: number[] } {
  if (isFcrMarket(market)) {
    return { maxBidLevels: MAX_BID_LEVELS_FCR, defaultPrices: DEFAULT_PRICES_FCR }
  }
  return { maxBidLevels: MAX_BID_LEVELS_MFRR, defaultPrices: DEFAULT_PRICES_MFRR }
}
